import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Share2, Megaphone, Palette, BarChart3, Presentation,
    Loader2, CheckCircle, Send, X, ChevronDown,
    AlertCircle, Crown, ArrowRight, Bot, User,
    Copy, Check, History, Plus, Trash2,
    ChevronLeft, Brain, Image, Sparkles,
    Download, ExternalLink, Maximize2, Wand2,
    Instagram, Facebook, Twitter, Linkedin, Youtube
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    DESIGN_CATEGORIES,
    IMAGE_SIZES,
    STYLE_PRESETS,
    generateDesignSessionId,
    hasDesignAccess,
    sendDesignMessage,
    fetchDesignSessions,
    loadDesignSession,
    deleteDesignSession,
    getDesignPromptSuggestions
} from '../services/DesignService';
import {
    fetchCreditBalance,
    CREDIT_THRESHOLDS,
    getCreditStatusColor,
    getEstimatedChatsRemaining
} from '../services/ChatUsageService';
import PropTypes from 'prop-types';
import AIReasoningPanel from './shared/AIReasoningPanel';

// Category icons mapping
const CATEGORY_ICONS = {
    social: Share2,
    ads: Megaphone,
    branding: Palette,
    infographic: BarChart3,
    presentation: Presentation
};

// Platform icons for size presets
const PLATFORM_ICONS = {
    'instagram-post': Instagram,
    'instagram-story': Instagram,
    'facebook-post': Facebook,
    'twitter-post': Twitter,
    'linkedin-post': Linkedin,
    'youtube-thumbnail': Youtube
};

// Gradient presets for visual appeal
const STYLE_GRADIENTS = {
    modern: 'from-slate-600 to-slate-800',
    bold: 'from-rose-500 to-orange-500',
    elegant: 'from-amber-400 to-yellow-600',
    playful: 'from-pink-500 to-purple-500',
    corporate: 'from-blue-600 to-indigo-600',
    vintage: 'from-amber-700 to-orange-800',
    minimalist: 'from-gray-400 to-gray-600',
    creative: 'from-fuchsia-500 to-cyan-500'
};

/**
 * Safe format credits - handles null/undefined
 */
const safeFormatCredits = (credits) => {
    if (credits === null || credits === undefined) return '...';
    if (credits >= 1000) {
        return `${(credits / 1000).toFixed(1)}k`;
    }
    return credits.toLocaleString();
};

/**
 * Image Preview Modal
 */
const ImagePreviewModal = ({ imageUrl, onClose }) => {
    if (!imageUrl) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="relative max-w-4xl max-h-[90vh]"
            >
                <button
                    onClick={onClose}
                    className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
                >
                    <X className="w-8 h-8" />
                </button>
                <img
                    src={imageUrl}
                    alt="Generated design"
                    className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                />
                <div className="absolute -bottom-14 left-0 right-0 flex justify-center gap-4">
                    <a
                        href={imageUrl}
                        download="design.png"
                        className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl backdrop-blur-sm transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Download className="w-4 h-4" />
                        Download
                    </a>
                    <a
                        href={imageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl backdrop-blur-sm transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ExternalLink className="w-4 h-4" />
                        Open
                    </a>
                </div>
            </motion.div>
        </motion.div>
    );
};

ImagePreviewModal.propTypes = {
    imageUrl: PropTypes.string,
    onClose: PropTypes.func.isRequired
};

/**
 * Upgrade Prompt Component - Creative and inspiring
 */
const UpgradePrompt = ({ onUpgrade }) => (
    <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-8 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-fuchsia-500/20 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-violet-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="max-w-md text-center relative z-10">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="w-24 h-24 rounded-3xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-cyan-500 flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-fuchsia-500/30"
            >
                <Wand2 className="w-12 h-12 text-white" />
            </motion.div>

            <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-3xl font-bold text-white mb-4"
            >
                Design Assistant
            </motion.h2>

            <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-slate-300 mb-8 text-lg"
            >
                Transform your ideas into stunning visuals with AI-powered design generation.
            </motion.p>

            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8 text-left border border-white/10"
            >
                <p className="text-sm font-medium text-white/90 mb-4">Unlock creative powers:</p>
                <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        AI-Powered Image Generation
                    </li>
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        Platform-Optimized Templates
                    </li>
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        Multiple Creative Styles
                    </li>
                    <li className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-white" />
                        </div>
                        Instant Social Media Graphics
                    </li>
                </ul>
            </motion.div>

            <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                onClick={onUpgrade}
                className="w-full px-8 py-4 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-500 text-white font-semibold rounded-2xl hover:shadow-lg hover:shadow-fuchsia-500/30 transition-all flex items-center justify-center gap-3 text-lg"
            >
                <Crown className="w-5 h-5" />
                Upgrade to Ultra
                <ArrowRight className="w-5 h-5" />
            </motion.button>

            <p className="text-xs text-slate-400 mt-4">
                Available on Ultra and Enterprise plans
            </p>
        </div>
    </div>
);

UpgradePrompt.propTypes = {
    onUpgrade: PropTypes.func.isRequired
};

/**
 * Generated Image Display Component
 */
const GeneratedImage = ({ url, onPreview }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    if (!url) return null;

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative group mt-4"
        >
            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-fuchsia-500/10 to-cyan-500/10 rounded-2xl">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-8 h-8 text-fuchsia-500 animate-spin" />
                        <span className="text-sm text-slate-500">Loading masterpiece...</span>
                    </div>
                </div>
            )}
            {hasError ? (
                <div className="flex items-center justify-center h-48 bg-slate-100 dark:bg-slate-800 rounded-2xl text-slate-500">
                    <AlertCircle className="w-6 h-6 mr-2" />
                    Failed to load image
                </div>
            ) : (
                <img
                    src={url}
                    alt="Generated design"
                    className={`w-full max-w-lg rounded-2xl shadow-xl cursor-pointer transition-all duration-300 hover:shadow-2xl hover:shadow-fuchsia-500/20 ${isLoading ? 'opacity-0 h-48' : 'opacity-100'}`}
                    onLoad={() => setIsLoading(false)}
                    onError={() => {
                        setIsLoading(false);
                        setHasError(true);
                    }}
                    onClick={() => onPreview(url)}
                />
            )}
            {!isLoading && !hasError && (
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200 flex gap-2">
                    <button
                        onClick={() => onPreview(url)}
                        className="p-2.5 bg-black/60 hover:bg-black/80 text-white rounded-xl backdrop-blur-sm transition-all"
                        title="View full size"
                    >
                        <Maximize2 className="w-4 h-4" />
                    </button>
                    <a
                        href={url}
                        download="design.png"
                        className="p-2.5 bg-black/60 hover:bg-black/80 text-white rounded-xl backdrop-blur-sm transition-all"
                        title="Download"
                    >
                        <Download className="w-4 h-4" />
                    </a>
                </div>
            )}
        </motion.div>
    );
};

GeneratedImage.propTypes = {
    url: PropTypes.string,
    onPreview: PropTypes.func.isRequired
};

/**
 * Style Preset Card
 */
const StyleCard = ({ style, isSelected, onClick }) => {
    const gradient = STYLE_GRADIENTS[style.id] || STYLE_GRADIENTS.modern;

    return (
        <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            className={`relative p-3 rounded-xl text-left transition-all ${isSelected
                ? 'ring-2 ring-fuchsia-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
        >
            <div className={`w-full h-12 rounded-lg bg-gradient-to-r ${gradient} mb-2`} />
            <p className="text-sm font-medium text-slate-900 dark:text-white">{style.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">{style.description}</p>
        </motion.button>
    );
};

StyleCard.propTypes = {
    style: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
    onClick: PropTypes.func.isRequired
};

/**
 * Main Design Agent Component
 */
const DesignAgent = ({ userEmail, subscription, onUpgradePlan, userCredits, onRefreshCredits }) => {
    const hasAccess = hasDesignAccess(subscription);

    // Settings state
    const [selectedCategory, setSelectedCategory] = useState('social');
    const [selectedStyle, setSelectedStyle] = useState('modern');
    const [selectedSize, setSelectedSize] = useState('instagram-post');
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);

    // Chat state
    const [sessionId, setSessionId] = useState(() => generateDesignSessionId());
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const [thinkingSteps, setThinkingSteps] = useState([]);
    const [streamingResponse, setStreamingResponse] = useState('');
    const [error, setError] = useState(null);
    const [copiedMessageIndex, setCopiedMessageIndex] = useState(null);

    // Image preview state
    const [previewImageUrl, setPreviewImageUrl] = useState(null);

    // Credit state
    const [credits, setCredits] = useState(null);
    const [lastCreditDeduction, setLastCreditDeduction] = useState(null);
    const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);

    // Chat history state
    const [chatSessions, setChatSessions] = useState([]);
    const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false);
    const [isLoadingSessions, setIsLoadingSessions] = useState(false);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [deletingSessionId, setDeletingSessionId] = useState(null);

    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Copy message to clipboard
    const handleCopyMessage = async (index, content) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopiedMessageIndex(index);
            setTimeout(() => setCopiedMessageIndex(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Extract image URLs from message content
    const extractImageUrl = (content) => {
        const match = content?.match(/!\[.*?\]\((.*?)\)/);
        return match ? match[1] : null;
    };

    // Format message content
    const formatMessageContent = (content) => {
        if (!content) return null;

        const imageUrl = extractImageUrl(content);
        if (imageUrl) {
            const textContent = content.replace(/!\[.*?\]\(.*?\)/g, '').trim();
            return (
                <div>
                    {textContent && (
                        <span className="whitespace-pre-wrap block mb-3">{textContent}</span>
                    )}
                    <GeneratedImage url={imageUrl} onPreview={setPreviewImageUrl} />
                </div>
            );
        }

        return <span className="whitespace-pre-wrap">{content}</span>;
    };

    // Fetch credits on mount
    useEffect(() => {
        if (!hasAccess) return;

        const loadCredits = async () => {
            try {
                const data = await fetchCreditBalance();
                setCredits(data.credits_balance);
            } catch (err) {
                console.error('Failed to fetch credits:', err);
                // Fallback to subscription credits when API fails
                const fallbackCredits = subscription?.aiCreditsRemaining ?? subscription?.credits ?? 0;
                setCredits(fallbackCredits);
            }
        };

        loadCredits();
    }, [hasAccess, subscription]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamingResponse]);

    // Load chat sessions when sidebar opens
    useEffect(() => {
        if (isHistorySidebarOpen && hasAccess) {
            loadSessions();
        }
    }, [isHistorySidebarOpen, hasAccess]);

    const loadSessions = async () => {
        setIsLoadingSessions(true);
        try {
            const data = await fetchDesignSessions();
            setChatSessions(data.sessions || []);
        } catch (err) {
            console.error('Failed to load sessions:', err);
        } finally {
            setIsLoadingSessions(false);
        }
    };

    const handleLoadSession = async (session) => {
        setIsLoadingSession(true);
        try {
            const data = await loadDesignSession(session.id);
            setSessionId(session.id);
            setMessages(data.messages || []);
            setIsHistorySidebarOpen(false);
        } catch (err) {
            console.error('Failed to load session:', err);
            setError('Failed to load conversation');
        } finally {
            setIsLoadingSession(false);
        }
    };

    const handleDeleteSession = async (sessionIdToDelete) => {
        setDeletingSessionId(sessionIdToDelete);
        try {
            await deleteDesignSession(sessionIdToDelete);
            setChatSessions(prev => prev.filter(s => s.id !== sessionIdToDelete));
            if (sessionId === sessionIdToDelete) {
                handleNewConversation();
            }
        } catch (err) {
            console.error('Failed to delete session:', err);
        } finally {
            setDeletingSessionId(null);
        }
    };

    const handleNewConversation = () => {
        setSessionId(generateDesignSessionId());
        setMessages([]);
        setThinkingSteps([]);
        setStreamingResponse('');
        setError(null);
        setIsHistorySidebarOpen(false);
    };

    const handleSendMessage = useCallback(async (messageText = inputMessage) => {
        if (!messageText.trim() || isStreaming) return;

        const userMessage = {
            role: 'user',
            content: messageText.trim(),
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsStreaming(true);
        setThinkingSteps([]);
        setStreamingResponse('');
        setError(null);

        try {
            let fullResponse = '';

            await sendDesignMessage(
                messageText.trim(),
                sessionId,
                {
                    category: selectedCategory,
                    style: selectedStyle,
                    size: selectedSize
                },
                {
                    onChunk: (chunk) => {
                        fullResponse += chunk;
                        setStreamingResponse(fullResponse);
                    },
                    onThinkingStep: (step) => {
                        setThinkingSteps(prev => {
                            const existing = prev.find(s => s.stepId === step.stepId);
                            if (existing) {
                                return prev.map(s => s.stepId === step.stepId ? step : s);
                            }
                            return [...prev, step];
                        });
                    },
                    onCreditInfo: (info) => {
                        if (info.charged) {
                            setLastCreditDeduction(info.charged);
                            setCredits(info.remaining);
                            setTimeout(() => setLastCreditDeduction(null), 3000);
                        }
                    },
                    onComplete: (result) => {
                        const assistantMessage = {
                            role: 'assistant',
                            content: result.content,
                            imageUrl: result.imageUrl,
                            timestamp: new Date().toISOString(),
                            thinkingSteps: [...thinkingSteps]
                        };
                        setMessages(prev => [...prev, assistantMessage]);
                        setStreamingResponse('');
                        setIsStreaming(false);

                        if (onRefreshCredits) {
                            onRefreshCredits();
                        }
                    },
                    onError: (err) => {
                        if (err.code === 'INSUFFICIENT_CREDITS') {
                            setShowInsufficientCredits(true);
                        } else {
                            setError(err.message);
                        }
                        setIsStreaming(false);
                    }
                }
            );
        } catch (err) {
            console.error('Design message error:', err);
            if (err.code === 'INSUFFICIENT_CREDITS') {
                setShowInsufficientCredits(true);
            } else {
                setError(err.message || 'Failed to generate design. Please try again.');
            }
            setIsStreaming(false);
        }
    }, [inputMessage, isStreaming, sessionId, selectedCategory, selectedStyle, selectedSize, thinkingSteps, onRefreshCredits]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    if (!hasAccess) {
        return <UpgradePrompt onUpgrade={onUpgradePlan} />;
    }

    const suggestions = getDesignPromptSuggestions(selectedCategory);
    const creditColor = credits !== null ? getCreditStatusColor(credits) : 'emerald';
    const estimatedChats = credits !== null ? getEstimatedChatsRemaining(credits) : 0;

    return (
        <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
            {/* Image Preview Modal */}
            <AnimatePresence>
                {previewImageUrl && (
                    <ImagePreviewModal
                        imageUrl={previewImageUrl}
                        onClose={() => setPreviewImageUrl(null)}
                    />
                )}
            </AnimatePresence>

            {/* Insufficient Credits Modal */}
            <AnimatePresence>
                {showInsufficientCredits && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowInsufficientCredits(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-md w-full shadow-2xl"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="flex items-center gap-4 mb-6">
                                <div className="p-3 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500">
                                    <Sparkles className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                                        Need More Credits
                                    </h3>
                                    <p className="text-sm text-slate-500">Time to refuel your creativity!</p>
                                </div>
                            </div>
                            <p className="text-slate-600 dark:text-slate-400 mb-8">
                                You've used up your design credits. Get more to continue creating amazing visuals.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowInsufficientCredits(false)}
                                    className="flex-1 px-5 py-3 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-all font-medium"
                                >
                                    Maybe Later
                                </button>
                                <button
                                    onClick={() => {
                                        setShowInsufficientCredits(false);
                                        onUpgradePlan?.();
                                    }}
                                    className="flex-1 px-5 py-3 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white rounded-xl hover:shadow-lg hover:shadow-fuchsia-500/30 transition-all font-medium"
                                >
                                    Get Credits
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200/50 dark:border-slate-800/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {/* History Toggle */}
                        <button
                            onClick={() => setIsHistorySidebarOpen(!isHistorySidebarOpen)}
                            className={`p-2.5 rounded-xl transition-all ${isHistorySidebarOpen
                                ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-500/30'
                                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                                }`}
                            title="Chat history"
                        >
                            <History className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-2xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-cyan-500 shadow-lg shadow-fuchsia-500/30">
                                <Wand2 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                                    Design Assistant
                                </h1>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Turn ideas into stunning visuals
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Credits Display */}
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${creditColor === 'red'
                            ? 'bg-gradient-to-r from-red-500/10 to-orange-500/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-500/30'
                            : creditColor === 'amber'
                                ? 'bg-gradient-to-r from-amber-500/10 to-yellow-500/10 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30'
                                : 'bg-gradient-to-r from-emerald-500/10 to-teal-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30'
                            }`}>
                            <Sparkles className="w-4 h-4" />
                            <span>{safeFormatCredits(credits)} credits</span>
                            <AnimatePresence>
                                {lastCreditDeduction && (
                                    <motion.span
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="text-red-500 font-bold"
                                    >
                                        -{lastCreditDeduction}
                                    </motion.span>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Settings Toggle */}
                        <button
                            onClick={() => setIsSettingsPanelOpen(!isSettingsPanelOpen)}
                            className={`p-2.5 rounded-xl transition-all ${isSettingsPanelOpen
                                ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-500/30'
                                : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                                }`}
                        >
                            <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isSettingsPanelOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* New Chat Button */}
                        <button
                            onClick={handleNewConversation}
                            className="p-2.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-all"
                            title="New conversation"
                        >
                            <Plus className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Settings Panel */}
                <AnimatePresence>
                    {isSettingsPanelOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                        >
                            <div className="pt-6 space-y-6">
                                {/* Style Selection - Visual Cards */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                                        Choose a Style
                                    </label>
                                    <div className="grid grid-cols-4 gap-3">
                                        {STYLE_PRESETS.map(style => (
                                            <StyleCard
                                                key={style.id}
                                                style={style}
                                                isSelected={selectedStyle === style.id}
                                                onClick={() => setSelectedStyle(style.id)}
                                            />
                                        ))}
                                    </div>
                                </div>

                                {/* Platform/Size Selection */}
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                                        Platform & Size
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.entries(IMAGE_SIZES).filter(([key]) => key !== 'custom').map(([key, size]) => {
                                            const Icon = PLATFORM_ICONS[key] || Image;
                                            const isSelected = selectedSize === key;
                                            return (
                                                <button
                                                    key={key}
                                                    onClick={() => setSelectedSize(key)}
                                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${isSelected
                                                        ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-500/30'
                                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                                                        }`}
                                                >
                                                    <Icon className="w-4 h-4" />
                                                    {size.label.split(' ')[0]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Main Content Area */}
            <div className="flex flex-1 min-h-0 overflow-hidden">
                {/* History Sidebar */}
                <AnimatePresence>
                    {isHistorySidebarOpen && (
                        <motion.div
                            initial={{ width: 0, opacity: 0 }}
                            animate={{ width: 300, opacity: 1 }}
                            exit={{ width: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="flex-shrink-0 border-r border-slate-200/50 dark:border-slate-800/50 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm overflow-hidden"
                        >
                            <div className="p-5 h-full flex flex-col">
                                <div className="flex items-center justify-between mb-5">
                                    <h3 className="font-semibold text-slate-900 dark:text-white">Your Designs</h3>
                                    <button
                                        onClick={() => setIsHistorySidebarOpen(false)}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-all"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                </div>

                                <button
                                    onClick={handleNewConversation}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-5 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white rounded-xl hover:shadow-lg hover:shadow-fuchsia-500/30 transition-all font-medium"
                                >
                                    <Wand2 className="w-4 h-4" />
                                    New Design
                                </button>

                                <div className="flex-1 overflow-y-auto space-y-2">
                                    {isLoadingSessions ? (
                                        <div className="flex justify-center py-8">
                                            <Loader2 className="w-6 h-6 text-fuchsia-500 animate-spin" />
                                        </div>
                                    ) : chatSessions.length === 0 ? (
                                        <div className="text-center py-8">
                                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 flex items-center justify-center mx-auto mb-3">
                                                <Image className="w-6 h-6 text-fuchsia-500" />
                                            </div>
                                            <p className="text-sm text-slate-500">
                                                No designs yet
                                            </p>
                                            <p className="text-xs text-slate-400 mt-1">
                                                Start creating something amazing!
                                            </p>
                                        </div>
                                    ) : (
                                        chatSessions.map(session => (
                                            <motion.div
                                                key={session.id}
                                                whileHover={{ scale: 1.02 }}
                                                className={`group p-4 rounded-xl cursor-pointer transition-all ${session.id === sessionId
                                                    ? 'bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 border border-fuchsia-500/30'
                                                    : 'hover:bg-slate-100 dark:hover:bg-slate-800/50'
                                                    }`}
                                                onClick={() => handleLoadSession(session)}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                                            {session.title || 'Untitled Design'}
                                                        </p>
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            {new Date(session.updatedAt).toLocaleDateString()}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteSession(session.id);
                                                        }}
                                                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 transition-all"
                                                        disabled={deletingSessionId === session.id}
                                                    >
                                                        {deletingSessionId === session.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </motion.div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col min-h-0">
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {messages.length === 0 && !isStreaming ? (
                            <div className="flex flex-col items-center justify-center h-full text-center px-4">
                                <motion.div
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="w-20 h-20 rounded-3xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-cyan-500 flex items-center justify-center mb-6 shadow-2xl shadow-fuchsia-500/30"
                                >
                                    <Wand2 className="w-10 h-10 text-white" />
                                </motion.div>

                                <motion.h2
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.1 }}
                                    className="text-2xl font-bold text-slate-900 dark:text-white mb-3"
                                >
                                    What will you create today?
                                </motion.h2>

                                <motion.p
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="text-slate-500 dark:text-slate-400 mb-8 max-w-md"
                                >
                                    Describe your vision and I'll bring it to life. From social posts to stunning graphics.
                                </motion.p>

                                {/* Creative Suggestions */}
                                <motion.div
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.3 }}
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl w-full"
                                >
                                    {suggestions.slice(0, 4).map((suggestion, idx) => (
                                        <motion.button
                                            key={idx}
                                            whileHover={{ scale: 1.02, y: -2 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => handleSendMessage(suggestion)}
                                            className="p-5 text-left bg-white dark:bg-slate-800/50 rounded-2xl border border-slate-200 dark:border-slate-700/50 hover:border-fuchsia-300 dark:hover:border-fuchsia-500/50 hover:shadow-lg hover:shadow-fuchsia-500/10 transition-all group"
                                        >
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 flex items-center justify-center mb-3 group-hover:from-fuchsia-500/20 group-hover:to-violet-500/20 transition-all">
                                                <Sparkles className="w-5 h-5 text-fuchsia-500" />
                                            </div>
                                            <span className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{suggestion}</span>
                                        </motion.button>
                                    ))}
                                </motion.div>
                            </div>
                        ) : (
                            <>
                                {messages.map((msg, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        {msg.role === 'assistant' && (
                                            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/20">
                                                <Bot className="w-5 h-5 text-white" />
                                            </div>
                                        )}
                                        <div
                                            className={`max-w-[75%] rounded-2xl px-5 py-4 ${msg.role === 'user'
                                                ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-500/20'
                                                : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 shadow-lg'
                                                }`}
                                        >
                                            {msg.thinkingSteps && msg.thinkingSteps.length > 0 && (
                                                <AIReasoningPanel steps={msg.thinkingSteps} isCollapsed accentColor="fuchsia" />
                                            )}
                                            <div className="text-sm leading-relaxed">
                                                {formatMessageContent(msg.content)}
                                            </div>
                                            {msg.role === 'assistant' && (
                                                <button
                                                    onClick={() => handleCopyMessage(idx, msg.content)}
                                                    className="mt-3 flex items-center gap-1.5 text-xs text-slate-400 hover:text-fuchsia-500 transition-colors"
                                                >
                                                    {copiedMessageIndex === idx ? (
                                                        <>
                                                            <Check className="w-3.5 h-3.5" />
                                                            Copied!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy className="w-3.5 h-3.5" />
                                                            Copy
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        {msg.role === 'user' && (
                                            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <User className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                                            </div>
                                        )}
                                    </motion.div>
                                ))}

                                {/* Streaming Response */}
                                {isStreaming && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex gap-4 justify-start"
                                    >
                                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 via-violet-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/20 animate-pulse">
                                            <Bot className="w-5 h-5 text-white" />
                                        </div>
                                        <div className="max-w-[75%] rounded-2xl px-5 py-4 bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700 shadow-lg">
                                            {thinkingSteps.length > 0 && (
                                                <AIReasoningPanel steps={thinkingSteps} accentColor="fuchsia" />
                                            )}
                                            {streamingResponse ? (
                                                <div className="text-sm leading-relaxed">
                                                    {formatMessageContent(streamingResponse)}
                                                </div>
                                            ) : thinkingSteps.length === 0 ? (
                                                <div className="flex items-center gap-3 text-sm text-slate-500">
                                                    <div className="flex gap-1">
                                                        <span className="w-2 h-2 bg-fuchsia-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                                        <span className="w-2 h-2 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                        <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                    </div>
                                                    Creating your design...
                                                </div>
                                            ) : null}
                                        </div>
                                    </motion.div>
                                )}

                                {/* Error Display */}
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-red-600 dark:text-red-400 text-sm"
                                    >
                                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                        <span className="flex-1">{error}</span>
                                        <button
                                            onClick={() => setError(null)}
                                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-500/20 rounded-lg transition-all"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </motion.div>
                                )}
                            </>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="flex-shrink-0 p-6 border-t border-slate-200/50 dark:border-slate-800/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
                        {/* Low Credits Warning */}
                        {credits !== null && credits < CREDIT_THRESHOLDS.LOW && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mb-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl flex items-center gap-3 text-amber-700 dark:text-amber-400"
                            >
                                <Sparkles className="w-5 h-5 flex-shrink-0" />
                                <span className="text-sm flex-1">Running low on credits. ~{estimatedChats} designs remaining.</span>
                                <button
                                    onClick={onUpgradePlan}
                                    className="text-sm font-semibold text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                                >
                                    Get More
                                </button>
                            </motion.div>
                        )}

                        <div className="flex gap-4">
                            <div className="flex-1 relative">
                                <textarea
                                    ref={inputRef}
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Describe the design you want to create..."
                                    className="w-full px-5 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400 shadow-sm transition-all"
                                    rows={2}
                                    disabled={isStreaming}
                                />
                            </div>
                            <button
                                onClick={() => handleSendMessage()}
                                disabled={!inputMessage.trim() || isStreaming}
                                className="px-6 py-4 bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white rounded-2xl hover:shadow-lg hover:shadow-fuchsia-500/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all flex items-center gap-2 font-medium"
                            >
                                {isStreaming ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Wand2 className="w-5 h-5" />
                                        Create
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

DesignAgent.propTypes = {
    userEmail: PropTypes.string,
    subscription: PropTypes.object,
    onUpgradePlan: PropTypes.func,
    userCredits: PropTypes.number,
    onRefreshCredits: PropTypes.func
};

export default DesignAgent;
